<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'

import SmartSearchResults from '@/components/map/SmartSearchResults.vue'
import BaseBottomSheet from '@/components/ui/BaseBottomSheet.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import type { useSmartSearch } from '@/composables/useSmartSearch'
import { FLOATING_SHEET_BOTTOM_OFFSET, mobileSheetMaxHeight } from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'SmartSearchResultsSheet' })

// MapOut-style results drawer: a draggable bottom sheet that shows the search results while the
// map (and result pins) stay visible above it. The search field lives in the separate top bar.
const props = defineProps<{
  search: ReturnType<typeof useSmartSearch>
  /** While the input is focused (keyboard up) the drawer drops to peek so the map stays visible. */
  inputFocused?: boolean
}>()

const { shouldShowResultsDropdown } = props.search

// Track the visual viewport so the drawer shrinks above the on-screen keyboard.
const viewportHeight = ref(
  typeof window === 'undefined' ? 844 : (window.visualViewport?.height ?? window.innerHeight),
)
// Height of the on-screen keyboard: the gap between the (stable) layout viewport and the shrunken
// visual viewport. Used to float the drawer above the keyboard instead of behind it.
const keyboardInset = ref(0)
const updateViewportHeight = () => {
  const vv = window.visualViewport
  viewportHeight.value = vv?.height ?? window.innerHeight
  keyboardInset.value = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0
}
// >120px filters out small layout-viewport shifts (e.g. the iOS URL bar) — only a real keyboard.
const keyboardOpen = computed(() => keyboardInset.value > 120)

// Reserve the top region for the pinned input bar so `full` never covers it.
const TOP_RESERVE = 132
const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  return {
    peek: 112,
    mid: Math.floor(height * 0.45),
    full: Math.max(Math.floor(height * 0.45), height - TOP_RESERVE),
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: 'mid',
})

// Keyboard coordination: keep results at `mid` whether or not the input is focused — the drawer
// floats above the keyboard (see `sheetStyle.bottom`), so there's no need to drop to peek (which
// previously hid the results behind the keyboard, making them unusable).
watch(
  () => props.inputFocused,
  () => sheet.snapTo('mid'),
)

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(
    keyboardOpen.value ? `calc(${keyboardInset.value}px + 8px)` : FLOATING_SHEET_BOTTOM_OFFSET,
  ),
  // When the keyboard is open, anchor the drawer just above it (8px gap) instead of at the tab-bar
  // offset, which would put the results behind the keyboard.
  bottom: keyboardOpen.value
    ? `calc(${keyboardInset.value}px + 8px)`
    : FLOATING_SHEET_BOTTOM_OFFSET,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

onMounted(() => {
  window.visualViewport?.addEventListener('resize', updateViewportHeight)
})
onBeforeUnmount(() => {
  window.visualViewport?.removeEventListener('resize', updateViewportHeight)
})
useEventListener(window, 'resize', updateViewportHeight)
</script>

<template>
  <BaseBottomSheet
    :sheet="sheet"
    title="Search"
    :sheet-style="sheetStyle"
    testid="search-results-sheet"
  >
    <!-- Results / empty hint -->
    <SmartSearchResults v-if="shouldShowResultsDropdown" :search="search" />
    <p v-else class="px-4 py-10 text-center text-sm text-gray-500">
      Search places, collections, activities, and routes.
    </p>
  </BaseBottomSheet>
</template>
